import { useState, useEffect, useCallback, useMemo } from 'react';
import inputSignalRulesService from 'services/inputSignalRulesService';
import inputSignalRuleSubscriptionService from 'services/inputSignalRuleSubscriptionService';
import { useNotificationContext } from 'contexts/NotificationContext';

const useInputSignalRules = () => {
	const [allAlerts, setAllAlerts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [filter, setFilter] = useState('subscribed');

	// Used for individual subscription event errors
	// so user can still view and interact w. alerts list
	const { showNotification } = useNotificationContext();

	const subscribedAlerts = useMemo(
		() => allAlerts.filter((alert) => alert.subscribed),
		[allAlerts]
	);

	const fetchAlerts = useCallback(async () => {
		setLoading(true);
		setError('');

		try {
			const data = await inputSignalRulesService.getInputSignalRules();
			setAllAlerts(Array.isArray(data) ? data : []);
		} catch (err) {
			console.error('Error fetching input signal rules:', err);
			setError('Failed to load alerts. Please try again later');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAlerts();
	}, [fetchAlerts]);

	const updateAlert = useCallback((alertId, changes) => {
		setAllAlerts((prev) =>
			prev.map((alert) =>
				alert.id === alertId ? { ...alert, ...changes } : alert
			)
		);
	}, []);

	const subscribe = useCallback(
		async (alert) => {
			// optimistically mark as subscribed
			updateAlert(alert.id, {
				subscribed: true,
				subscribed_at: new Date().toISOString(),
				subscription_count: (alert.subscription_count ?? 0) + 1,
			});

			try {
				const subscription =
					await inputSignalRuleSubscriptionService.createSubscription(alert.id);
				// patch in the real subscription data from the response
				updateAlert(alert.id, {
					subscription_id: subscription.id,
					subscribed_at: subscription.created_at,
				});
			} catch (err) {
				// rollback
				updateAlert(alert.id, {
					subscribed: false,
					subscribed_at: null,
					subscription_id: null,
					subscription_count: alert.subscription_count,
				});
				
				if(err.statusCode === 403) {
					showNotification('You do not have the correct permissions for the report related to this alert and cannot subscribe to it', 'error');
				} else {
					showNotification('Failed to subscribe to alert', 'error');
				}
			}
		},
		[updateAlert, showNotification]
	);

	const unsubscribe = useCallback(
		async (alert) => {
			// optimistically mark as unsubscribed
			updateAlert(alert.id, {
				subscribed: false,
				subscribed_at: null,
				subscription_id: null,
				subscription_count: Math.max((alert.subscription_count ?? 0) - 1, 0),
			});

			try {
				await inputSignalRuleSubscriptionService.deleteSubscription(
					alert.subscription_id
				);
			} catch (err) {
				// rollback
				updateAlert(alert.id, {
					subscribed: true,
					subscribed_at: alert.subscribed_at,
					subscription_id: alert.subscription_id,
					subscription_count: alert.subscription_count,
				});
				showNotification('Failed to unsubscribe from alert', 'error');
			}
		},
		[updateAlert, showNotification]
	);

	const submitDelete = useCallback(
		async (alertToDelete) => {
			const preDeleteAlerts = structuredClone(allAlerts);
			const postDeleteAlerts = allAlerts.filter(alert => alertToDelete.id !== alert.id);
	
			// Optimistic removal
			setAllAlerts(postDeleteAlerts);

			try {
				await inputSignalRulesService.deleteInputSignalRule(
					alertToDelete.id
				);
				showNotification(
					'Alert Rule deleted',
					'success'
				);
			} catch (err) {
				// rollback
				setAllAlerts(preDeleteAlerts);

				showNotification('Failed to delete alert', 'error');
			}
		},
		[allAlerts, setAllAlerts, showNotification]
	);
		

	return {
		subscribedAlerts,
		allAlerts,
		loading,
		error,
		subscribe,
		unsubscribe,
		submitDelete,
		filter,
		setFilter,
	};
};

export default useInputSignalRules;
